/*
 * The two-way message contract between the Asset Library's Office Dialog
 * window and the task pane that opened it.
 *
 * An Office Dialog has none of the pane's React tree (no AuthStateProvider,
 * no bearer token) and can never call PowerPoint.run(), so both halves of
 * "browse and insert" happen in the pane while the UI stays in the dialog.
 * Office.js gives exactly two channels, both size-capped (SIGNALS only):
 *
 *   dialog -> pane   Office.context.ui.messageParent(string)
 *   pane   -> dialog Dialog.messageChild(string)   [DialogApi 1.2+]
 *
 * Brand Templates' manifest travels via localStorage; only ids cross the
 * wire. Slide/Presentation Templates don't: the pane's reply to
 * "ready"/"refresh" hands over a SAS token instead, and the dialog fetches
 * the whole library itself directly over the network with it. Messages are
 * JSON strings in both directions, and both parsers treat what they receive
 * as untrusted — a window and a pane can be running different add-in builds.
 */

import { logError, scrubMessage } from "../../../../../telemetry";

const ASSET_LIBRARY_CHANNEL_CONNECTOR = "deckorate";

/** Shown when the pane reports a failure without a usable message. */
export const FALLBACK_SAS_ERROR = "Couldn't load slide templates.";
export const FALLBACK_INSERT_ERROR = "Couldn't insert the selected template(s).";
export const FALLBACK_BRAND_TEMPLATES_ERROR = "Couldn't load brand templates.";
export const FALLBACK_PRESENTATION_SAS_ERROR = "Couldn't load presentation templates.";
export const FALLBACK_PRESENTATION_INSERT_ERROR = "Couldn't insert the selected slide(s).";
export const FALLBACK_BRAND_INSERT_ERROR = "Couldn't insert the selected layout(s).";

/** Sent by the dialog window to the task pane. */
export type DialogToPaneMessage =
  /** The picker has mounted and wants a SAS token to fetch the library with. */
  | { readonly type: "ready" }
  /** The user hit Retry — mint a fresh SAS token (the old one may have
   *  expired) and reply again. */
  | { readonly type: "refresh" }
  /** Insert these templates into the deck. */
  | { readonly type: "insert"; readonly ids: readonly string[] }
  /** The Brand Templates tab mounted (or was retried) and wants the list —
   *  see brandTemplatesHandoff.ts. Only the pane can authorise this fetch. */
  | { readonly type: "brand-templates-request" }
  | { readonly type: "brand-templates-refresh" }
  /**
   * The user picked a brand template to use. The Asset Library never applies
   * anything itself — the pane parks this pick (pendingSavedTemplateSelection.ts),
   * navigates to Apply Brand's Saved Templates tab, and closes this window.
   */
  | { readonly type: "switch-brand-template"; readonly id: string }
  /**
   * The Brand Templates preview noticed the template it's showing has
   * vanished from the library (EC-08) — the pane clears its own selection if
   * it matches and shows the "no longer available" message, so it's already
   * in sync by the time the user closes this window.
   */
  | { readonly type: "brand-template-unavailable"; readonly id: string }
  /** Insert these 1-based layouts of one brand template after the current slide. */
  | { readonly type: "brand-templates-insert"; readonly templateId: string; readonly slideIndexes: readonly number[] }
  /**
   * Presentation Templates tab equivalents of "ready"/"refresh"/"insert"
   * above — namespaced distinctly so the two tabs' messages never collide on
   * one connection (same SAS-token-then-direct-fetch pattern this module's
   * own header describes).
   */
  | { readonly type: "presentation-templates-ready" }
  | { readonly type: "presentation-templates-refresh" }
  | { readonly type: "presentation-templates-insert"; readonly ids: readonly string[] }
  /** The user closed the picker; the pane should tear the window down. */
  | { readonly type: "close" };

/** Sent by the task pane to the dialog window. */
export type PaneToDialogMessage =
  /** A SAS token is ready — the dialog fetches the whole library itself
   *  directly with it (see this module's own header and
   *  slide-templates/services/slideTemplatesApi.ts's `SlideTemplatesSasInfo`). */
  | { readonly type: "sas-ready"; readonly sasUrl: string; readonly manifestBlobPath: string }
  /** The SAS mint failed; show this and offer Retry. */
  | { readonly type: "sas-error"; readonly message: string }
  /** The requested slides were inserted. */
  | { readonly type: "insert-done"; readonly inserted: number }
  /** The insert failed; show this in the picker (the window stays open). */
  | { readonly type: "insert-error"; readonly message: string }
  /** The brand template list has been staged in brandTemplatesHandoff.ts. */
  | { readonly type: "brand-templates-ready" }
  /** The brand template list couldn't be fetched or staged; show this and offer Retry. */
  | { readonly type: "brand-templates-error"; readonly message: string }
  /** Brand Templates insert replies — the window closes itself on a pane-side
   *  success or notice, so these only settle a request that stays open. */
  | { readonly type: "brand-insert-done"; readonly inserted: number }
  | { readonly type: "brand-insert-error"; readonly message: string }
  /** Presentation Templates tab equivalents of the insert reply above, plus
   *  its own SAS-token reply in place of a staged manifest — see the
   *  DialogToPaneMessage variants' doc comment. */
  | { readonly type: "presentation-sas-ready"; readonly sasUrl: string; readonly folderPath: string | null }
  | { readonly type: "presentation-sas-error"; readonly message: string }
  | { readonly type: "presentation-insert-done"; readonly inserted: number }
  | { readonly type: "presentation-insert-error"; readonly message: string };

/** Extracts the JSON string payload from either channel's event argument. */
function payloadOf(arg: unknown): string | null {
  const message = (arg as { message?: unknown } | null)?.message;
  return typeof message === "string" ? message : null;
}

/** Narrows an unknown array to a non-empty list of non-empty strings. */
function toIds(raw: unknown): readonly string[] | null {
  if (!Array.isArray(raw)) return null;
  const ids = raw.filter((id): id is string => typeof id === "string" && id.length > 0);
  return ids.length > 0 ? ids : null;
}

/** Narrows an unknown array to a non-empty list of positive whole numbers. */
function toSlideIndexes(raw: unknown): readonly number[] | null {
  if (!Array.isArray(raw)) return null;
  const indexes = raw.filter((n): n is number => typeof n === "number" && Number.isInteger(n) && n > 0);
  return indexes.length > 0 ? indexes : null;
}

/** Narrows an unknown value to a non-empty string, or null. */
function toNonEmptyString(raw: unknown): string | null {
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

/** Narrows an unknown value to a non-empty string, falling back when absent. */
function toStringWithFallback(raw: unknown, fallback: string): string {
  return toNonEmptyString(raw) ?? fallback;
}

/** Attempts to JSON.parse `raw`, returning undefined on failure. */
function tryParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch (error) {
    // A malformed payload here means a mismatched dialog/pane build or a
    // corrupted channel message, not user input — worth tracing.
    logError(
      "error.connector.failure",
      {
        type: error instanceof Error ? error.name : "AssetLibraryChannelParseError",
        scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
      },
      { connectorSystemName: ASSET_LIBRARY_CHANNEL_CONNECTOR, reason: "asset-library-channel-message-parse-failed" },
    );
    return undefined;
  }
}

/** Parses a message received by the PANE. Returns null if it isn't one of ours. */
export function parseDialogToPaneMessage(arg: unknown): DialogToPaneMessage | null {
  const raw = payloadOf(arg);
  if (raw === null) return null;
  const parsed = tryParseJson(raw);
  if (parsed === undefined) return null;

  const message = parsed as {
    type?: unknown;
    ids?: unknown;
    id?: unknown;
    templateId?: unknown;
    slideIndexes?: unknown;
  } | null;
  switch (message?.type) {
    case "ready":
      return { type: "ready" };
    case "refresh":
      return { type: "refresh" };
    case "close":
      return { type: "close" };
    case "brand-templates-request":
      return { type: "brand-templates-request" };
    case "brand-templates-refresh":
      return { type: "brand-templates-refresh" };
    case "insert": {
      const ids = toIds(message.ids);
      // An insert with no usable ids is indistinguishable from a no-op; treat
      // it as unrecognised rather than inserting nothing and reporting success.
      return ids ? { type: "insert", ids } : null;
    }
    case "switch-brand-template": {
      const id = toNonEmptyString(message.id);
      return id ? { type: "switch-brand-template", id } : null;
    }
    case "brand-template-unavailable": {
      const id = toNonEmptyString(message.id);
      return id ? { type: "brand-template-unavailable", id } : null;
    }
    case "brand-templates-insert": {
      const templateId = toNonEmptyString(message.templateId);
      const slideIndexes = toSlideIndexes(message.slideIndexes);
      // Same reasoning as "insert": nothing usable to insert is unrecognised, not a success.
      return templateId && slideIndexes ? { type: "brand-templates-insert", templateId, slideIndexes } : null;
    }
    case "presentation-templates-ready":
      return { type: "presentation-templates-ready" };
    case "presentation-templates-refresh":
      return { type: "presentation-templates-refresh" };
    case "presentation-templates-insert": {
      const ids = toIds(message.ids);
      return ids ? { type: "presentation-templates-insert", ids } : null;
    }
    default:
      return null;
  }
}

/** Parses a message received by the DIALOG. Returns null if it isn't one of ours. */
export function parsePaneToDialogMessage(arg: unknown): PaneToDialogMessage | null {
  const raw = payloadOf(arg);
  if (raw === null) return null;
  const parsed = tryParseJson(raw);
  if (parsed === undefined) return null;

  const message = parsed as {
    type?: unknown;
    message?: unknown;
    inserted?: unknown;
    id?: unknown;
    sasUrl?: unknown;
    manifestBlobPath?: unknown;
    folderPath?: unknown;
  } | null;
  switch (message?.type) {
    case "sas-ready": {
      const sasUrl = toNonEmptyString(message.sasUrl);
      const manifestBlobPath = toNonEmptyString(message.manifestBlobPath);
      if (!sasUrl || !manifestBlobPath) return null;
      return { type: "sas-ready", sasUrl, manifestBlobPath };
    }
    case "sas-error":
      return { type: "sas-error", message: toStringWithFallback(message.message, FALLBACK_SAS_ERROR) };
    case "insert-done":
      return { type: "insert-done", inserted: typeof message.inserted === "number" ? message.inserted : 0 };
    case "insert-error":
      return { type: "insert-error", message: toStringWithFallback(message.message, FALLBACK_INSERT_ERROR) };
    case "brand-templates-ready":
      return { type: "brand-templates-ready" };
    case "brand-templates-error":
      return { type: "brand-templates-error", message: toStringWithFallback(message.message, FALLBACK_BRAND_TEMPLATES_ERROR) };
    case "brand-insert-done":
      return { type: "brand-insert-done", inserted: typeof message.inserted === "number" ? message.inserted : 0 };
    case "brand-insert-error":
      return { type: "brand-insert-error", message: toStringWithFallback(message.message, FALLBACK_BRAND_INSERT_ERROR) };
    case "presentation-sas-ready": {
      const sasUrl = toNonEmptyString(message.sasUrl);
      if (!sasUrl) return null;
      const folderPath = toNonEmptyString(message.folderPath);
      return { type: "presentation-sas-ready", sasUrl, folderPath };
    }
    case "presentation-sas-error":
      return {
        type: "presentation-sas-error",
        message: toStringWithFallback(message.message, FALLBACK_PRESENTATION_SAS_ERROR),
      };
    case "presentation-insert-done":
      return { type: "presentation-insert-done", inserted: typeof message.inserted === "number" ? message.inserted : 0 };
    case "presentation-insert-error":
      return {
        type: "presentation-insert-error",
        message: toStringWithFallback(message.message, FALLBACK_PRESENTATION_INSERT_ERROR),
      };
    default:
      return null;
  }
}

export function serialiseMessage(message: DialogToPaneMessage | PaneToDialogMessage): string {
  return JSON.stringify(message);
}
